import {
  BILLING_STATUS,
  SUBSCRIPTION_PAYMENT_PLAN,
  SUBSCRIPTION_STATUS,
  TypeSubscriptionRecord,
} from '@/lib/api/subscriptionManager/types';
import {
  isBabylistSubscription,
  isGroupGiftingMigratedSubscription,
} from '@/lib/api/subscriptionManager/helpers/subscriptionSourceHelpers';
import { getScheduledItems } from '@/lib/api/subscriptionManager/helpers/scheduledItemHelpers';

export function filterInvalidSubscriptionTypes(
  subscriptions: TypeSubscriptionRecord[],
) {
  const invalidStatuses = new Set([
    SUBSCRIPTION_STATUS.INACTIVE,
    SUBSCRIPTION_STATUS.CREATED,
  ]);

  return subscriptions?.filter(
    (s) => !invalidStatuses.has(s.subscription_status),
  );
}

export function filterValidSubscriptionTypes(
  subscriptions: TypeSubscriptionRecord[],
) {
  const validStatuses = new Set([
    SUBSCRIPTION_STATUS.ACTIVE,
    SUBSCRIPTION_STATUS.FINISHED,
    SUBSCRIPTION_STATUS.CANCELLED,
  ]);

  return subscriptions?.filter((s) => validStatuses.has(s.subscription_status));
}

export function filterCancelledSubscriptions(
  subscriptions: TypeSubscriptionRecord[],
) {
  return subscriptions?.filter(isSubscriptionCancelled);
}

export function isActiveSubscriber(subscriptions: TypeSubscriptionRecord[]) {
  return subscriptions?.some(isSubscriptionActive);
}

export function isAgedOutSubscriber(subscriptions: TypeSubscriptionRecord[]) {
  return subscriptions?.some(isSubscriptionFinished);
}

export function isAgedOutFullSubscriber(
  subscriptions: TypeSubscriptionRecord[],
) {
  return subscriptions?.some(isSubscriptionReactivationAllowed);
}

export function isSubscriptionActive(subscription: TypeSubscriptionRecord) {
  return subscription.subscription_status === SUBSCRIPTION_STATUS.ACTIVE;
}

export function isSubscriptionCancelled(subscription: TypeSubscriptionRecord) {
  return subscription.subscription_status === SUBSCRIPTION_STATUS.CANCELLED;
}

export function isSubscriptionFinished(subscription: TypeSubscriptionRecord) {
  return subscription.subscription_status === SUBSCRIPTION_STATUS.FINISHED;
}

export function isSubscriptionCancellationAllowed(
  subscription: TypeSubscriptionRecord,
) {
  return isSubscriptionActive(subscription);
}

export function isSubscriptionOnlyCancellableWithCXIntervention(
  subscription: TypeSubscriptionRecord,
) {
  return (
    isBabylistSubscription(subscription) ||
    isGroupGiftingMigratedSubscription(subscription)
  );
}

export function isSubscriptionReactivationAllowed(
  subscription: TypeSubscriptionRecord,
) {
  const { CANCELLED, FINISHED } = SUBSCRIPTION_STATUS;
  const isPartialSubscription = subscription.partial_subscription;

  return (
    [CANCELLED, FINISHED].includes(subscription.subscription_status) &&
    !isPartialSubscription
  );
}

export function determineSubscriptionPaymentPlan(
  subscription: TypeSubscriptionRecord,
) {
  const isRepufEnabledForSubscription = subscription.bundle_discount_uuid;
  const unprocessedScheduledItems = getScheduledItems(subscription, {
    filterProcessedItems: true,
  });
  const nextUnprocessedItem = unprocessedScheduledItems?.[0];

  if (nextUnprocessedItem?.billing_status === BILLING_STATUS.PREPAY) {
    if (isRepufEnabledForSubscription) {
      return SUBSCRIPTION_PAYMENT_PLAN.REPUF;
    }

    return SUBSCRIPTION_PAYMENT_PLAN.PUF;
  } else {
    return SUBSCRIPTION_PAYMENT_PLAN.PAYGO;
  }
}
